import {
  DataGridPro,
  DataGridProProps,
  GridRowModesModel,
  GridToolbar,
  GridActionsCellItem,
  GridColDef,
} from "@mui/x-data-grid-pro";
import { debounce } from "lodash";
import { createElement, useCallback, useEffect, useState } from "react";
import { defaultPageSizeOptions } from "../../../constants/paginationConstants";
import dayjs from "dayjs";
import { exportCsvDateFormat } from "../../../constants/dateFormats";
import {
  Box,
  Button,
  Checkbox,
  Chip,
  IconButton,
  Link,
  Tooltip,
} from "@mui/material";
import * as MuiIcons from "@mui/icons-material";
import moment from "moment";
import { usePermissionWrapper } from "../../../hooks/usePermissionWrapper";
import { GridCellExpand } from "./ExpandCell";

interface CustomDataGridPros extends DataGridProProps {
  paging?: boolean;
  rowsCount?: number;
  dataTableProps?: any;
  showQuickFilter?: boolean;
  filterValues?: any;
  exportFileName?: string;
  hasDefinedColumns?: boolean;
  actions?: object;
  columnVisibility?: any;
}

export const DataGridComponent: React.FC<CustomDataGridPros> = ({
  columns,
  rows,
  apiRef,
  checkboxSelection,
  checkboxSelectionVisibleOnly,
  columnVisibility = {},
  disableColumnSelector,
  autoHeight,
  disableRowSelectionOnClick = true,
  loading,
  sx,
  onRowSelectionModelChange,
  pageSizeOptions = defaultPageSizeOptions,
  paginationModel,
  onPaginationModelChange,
  dataTableProps,
  filterValues,
  paginationMode = "client",
  rowsCount,
  pagination = true,
  showQuickFilter = false,
  rowSelectionModel,
  filterMode = "client",
  sortingMode = "client",
  sortModel,
  onSortModelChange,
  exportFileName = "Sample_Management_System",
  editMode,
  processRowUpdate,
  hasDefinedColumns = true,
  actions = [],
  ...props
}: any) => {
  const { checkPermission } = usePermissionWrapper();
  const datePreference = "DD/MM/YYYY";
  let customColumns: GridColDef<any>[] = [];
  if (hasDefinedColumns) {
    customColumns = columns;
  } else {
    for (const col of columns) {
      if (col?.cmp === "link") {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => {
          const permission = col?.linkPermission
            ? checkPermission(col?.linkPermission)
            : true;
          return (
            <Box>
              {col.badge && (params.row.isNew || params.row.isUpdated) && (
                <span
                  className={`inCellBadge ${
                    params.row.isNew
                      ? "new"
                      : params.row.isUpdated
                      ? "updated"
                      : ""
                  }`}
                >
                  {params.row.isUpdated ? "Updated" : "New"}
                </span>
              )}
              {permission ? (
                <Tooltip
                  title={
                    params.row[col.field]?.length > 36
                      ? params.row[col.field]
                      : null
                  }
                  placement="top"
                  arrow
                >
                  <Link
                    onClick={() => actions[col.field](params.row)}
                    sx={{
                      cursor: "pointer",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      display: "-webkit-box",
                      "-webkitLineClamp": "2",
                      "-webkitBoxOrient": "vertical",
                    }}
                  >
                    {params.row[col.field]}
                  </Link>
                </Tooltip>
              ) : (
                <Box>{params.row[col.field]}</Box>
              )}
            </Box>
          );
        };
        customColumns.push(tempCol);
      } else if (col?.cmp === "chip") {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => {
          return (
            <Chip
              label={
                col?.isCodelistValue
                  ? col?.valueOptions?.find(
                      (e: any) => e?.value == params.row?.[col.field]
                    )?.label
                  : params.row[col.field]
              }
              size="small"
              variant="outlined"
              color="info"
            />
          );
        };
        customColumns.push(tempCol);
      } else if (col?.cmp === "checkbox") {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => {
          return (
            <Tooltip title={params.row?.tooltipText} arrow>
              <Checkbox
                disabled={params.row[`${col.field}_isDisabled`]}
                checked={params.row[col.field]}
                onChange={() => actions[col.field](params.row)}
                inputProps={{ "aria-label": "controlled" }}
                size="small"
              />
            </Tooltip>
          );
        };
        customColumns.push(tempCol);
      } else if (col?.cmp === "button") {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => {
          return (
            <Button
              onClick={() => actions[col.field](params.row)}
              variant="outlined"
            >
              {params.row[col.buttonText]}
            </Button>
          );
        };
        customColumns.push(tempCol);
      } else if (col?.cmp === "icon") {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => {
          const handleIconClick = () => {
            actions[col.field](params.row);
          };
          return (
            <Box display="flex" px={4} justifyContent="center" mx="auto">
              <Tooltip title={tempCol.tooltipTitle} placement="top" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={handleIconClick}
                  sx={{ fontSize: "small" }}
                >
                  {createElement((MuiIcons as any)[tempCol.iconName])}
                </IconButton>
              </Tooltip>
            </Box>
          );
        };
        customColumns.push(tempCol);
      } else if (col?.cmp === "date" || col?.cmp === "dateTime") {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => {
          if (params.row[col.field]) {
            if (col?.cmp === "dateTime") {
              return moment(params.row[col.field]).format(
                `${datePreference.toUpperCase()} h:mm A`
              );
            } else {
              return moment(params.row[col.field]).format(
                `${datePreference.toUpperCase()}`
              );
            }
          } else {
            return null;
          }
        };
        customColumns.push(tempCol);
      } else if (col?.cmp === "array") {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => {
          return <Box>{params.row[col.field]?.join(",")}</Box>;
        };
        customColumns.push(tempCol);
      } else if (col?.cmp === "actions") {
        const tempCol = { ...col };
        tempCol.getActions = (params: any) => {
          return col.actionsArray?.map((item: any) => (
            <GridActionsCellItem
              key={params.row.id}
              icon={createElement((MuiIcons as any)[item.icon])}
              label={item.label}
              onClick={() => actions[col.field][item.key](params.row)}
              showInMenu
              disabled={
                item?.permission ? !checkPermission(item?.permission) : false
              }
            />
          ));
        };

        customColumns.push(tempCol);
      } else {
        const tempCol = { ...col };
        tempCol.renderCell = (params: any) => (
          <>
            {col?.isCodelistValue && params?.colDef?.valueOptions ? (
              <GridCellExpand
                value={
                  params?.colDef?.valueOptions?.find(
                    (e: any) => e?.value == params.row?.[col.field]
                  )?.label
                }
                width={params?.colDef?.width}
              />
            ) : (
              <GridCellExpand
                value={params.row[col.field]}
                width={params?.colDef?.width}
              />
            )}
          </>
        );
        customColumns.push(tempCol);
      }
    }
  }

  const getDetailPanelHeight = useCallback(() => "auto", []);

  useEffect(() => {
    if (sortModel) {
      onPaginationModelChange({ page: 0, pageSize: paginationModel?.pageSize });
    }
  }, [sortModel]);

  const numberOperators = ["=", "!=", ">", ">=", "<", "<="];

  const filterObj = (data: any) => {
    if (filterValues) {
      let filtersArray = data.items
        .map((obj: any) => {
          if (numberOperators.includes(obj.operator)) {
            obj.value = obj.value ? parseInt(obj.value) : undefined;
          } else if (typeof obj.value === "string") {
            obj.value = obj.value.trim();
            if (obj.value === "") {
              obj.value = null; // Set null or handle as required
            }
          }
          if (
            obj.value == 0 ||
            obj.value ||
            obj.operator == "isEmpty" ||
            obj.operator == "isNotEmpty"
          ) {
            return {
              name: obj.field,
              value:
                obj.operator == "isEmpty" || obj.operator == "isNotEmpty"
                  ? null
                  : obj.value,
              operation: obj.operator,
            };
          }
          return;
        })
        .filter((ele: any) => ele);
      filtersArray = filtersArray.filter(
        (item: { operation: string; value: any }) =>
          !(
            item.operation != "isEmpty" &&
            item.operation != "isNotEmpty" &&
            (item.value === "" || item.value?.length == 0)
          )
      );
      filterValues({
        advanceFilter: {
          filters: filtersArray,
          type: `${data?.logicOperator?.toUpperCase()}`,
        },
        quickFilter: data.quickFilterValues.join(" "),
      });
    }
  };
  const debounceFn = debounce(filterObj, 900);

  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState(columnVisibility);

  return (
    <DataGridPro
      apiRef={apiRef}
      rows={rows}
      columns={customColumns}
      autoHeight={autoHeight}
      checkboxSelection={checkboxSelection}
      checkboxSelectionVisibleOnly={checkboxSelectionVisibleOnly}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel: any) => {
        setColumnVisibilityModel(newModel);
        const hideColumsArr: any = [];
        for (const obj of columns) {
          if (newModel[obj.field] == false) {
            hideColumsArr.push(false);
          } else {
            hideColumsArr.push(true);
          }
        }
      }}
      slots={{ toolbar: GridToolbar }}
      slotProps={{
        toolbar: {
          showQuickFilter,
          csvOptions: {
            fileName: `${exportFileName}_${dayjs().format(
              exportCsvDateFormat
            )}`,
          },
        },
      }}
      disableColumnSelector={disableColumnSelector}
      disableRowSelectionOnClick={disableRowSelectionOnClick}
      loading={loading}
      sx={sx}
      pagination={pagination}
      paginationMode={paginationMode}
      pageSizeOptions={pageSizeOptions}
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      onRowSelectionModelChange={onRowSelectionModelChange}
      getDetailPanelHeight={getDetailPanelHeight}
      {...(dataTableProps && dataTableProps)}
      filterMode={filterMode}
      onFilterModelChange={(newValue) => {
        debounceFn(newValue);
      }}
      rowSelectionModel={rowSelectionModel}
      rowCount={rowsCount}
      sortingMode={sortingMode}
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      rowModesModel={editMode && rowModesModel}
      onRowModesModelChange={editMode && handleRowModesModelChange}
      processRowUpdate={processRowUpdate}
      {...props}
    />
  );
};
